import type { CollectorUploadKind, ItemUploadDocument, UploadKind } from "../uploads/index.js";
import { parseAmount } from "./amount.js";

/**
 * Collector items (art, wine, whisky, trading card), headless. The app's
 * four schema forms (`frontend/mobile_app/.../schemas/*_form_schema.dart`)
 * ported as data: sections, fields, options, and reveal/required rules
 * written as conditions (never functions), so a tenant can configure them
 * later. Plus the validator, the notices (the trading-card cap and the
 * advisory review notices), the create payload, the Vault name and edit mode.
 *
 * Labels are i18n keys `collector.<category>.<key>`; options
 * `collector.<category>.options.<key>.<value>`; help text
 * `collector.<category>.help.<key>`. Form values live in a flat map: a
 * choice's specify input is `<key>Specify`; multi-selects are arrays.
 */

// ---------------------------------------------------------------- constants

/** Collector classification → `classId` (as `AssetType`). */
export const COLLECTOR_CLASS_IDS = {
  art: 8,
  wine: 9,
  whisky: 10,
  tradingCard: 11,
} as const;

export type CollectorCategory = keyof typeof COLLECTOR_CLASS_IDS;

export function isCollectorCategory(c: string | null | undefined): c is CollectorCategory {
  return !!c && Object.prototype.hasOwnProperty.call(COLLECTOR_CLASS_IDS, c);
}

export const COLLECTOR_SECTION_IDS = ["info", "grading", "storage", "documents"] as const;
export type CollectorSectionId = (typeof COLLECTOR_SECTION_IDS)[number];

/** Trading cards above this insured value can't be insured (the backend's `tradingCardMaxCents`). */
export const TRADING_CARD_CAPS: Readonly<Record<string, number>> = { SGD: 20_000, HKD: 115_000 };

/** Grades 1 to 10 in steps of 0.5. */
export const GRADE_OPTIONS: readonly string[] = Array.from({ length: 19 }, (_, i) => String(1 + i * 0.5));

// ---------------------------------------------------------------- schema types

export type CollectorValue = string | string[];
export type CollectorValues = Record<string, CollectorValue>;

/** Uploaded file counts per document kind (for `noFiles`). */
export type FileCounts = Partial<Record<UploadKind, number>>;

/** A reveal/required condition. Evaluated by `evalRule`; no functions in the schema. */
export type Rule =
  | { field: string; eq: string }
  | { field: string; in: readonly string[] }
  | { blank: string }
  | { noFiles: CollectorUploadKind }
  | { all: readonly Rule[] }
  | { any: readonly Rule[] };

export type CollectorFieldType =
  | "text"
  | "number"
  | "currency"
  | "date"
  /** One choice, as radios. */
  | "radio"
  /** One choice, as a Select. */
  | "select"
  | "multi"
  | "yesNo"
  /** 1–10 in 0.5 steps, as a Select. Sent as a number. */
  | "grade"
  /** Price per bottle × quantity: shown read-only, never sent. */
  | "total"
  /** A document kind (the key is the kind). */
  | "upload";

export interface CollectorOption {
  value: string;
  /**
   * Reveals `<key>Specify`. `true`: optional text (as in the app).
   * `"integer"`: required, a whole number above 0. `"amount"`: required, an
   * amount above 0.
   */
  specify?: true | "integer" | "amount";
}

export interface CollectorField {
  key: string;
  type: CollectorFieldType;
  /** `true`, or a rule (conditional). Absent: optional. */
  required?: boolean | Rule;
  revealWhen?: Rule;
  options?: readonly CollectorOption[];
  /** Has help text (`collector.<category>.help.<key>`). */
  help?: boolean;
  /** The required error's i18n key, when the generic one doesn't fit. */
  requiredKey?: string;
  /** `number`: the allowed range and its error. */
  range?: { min: number; max: number; messageKey: string };
  /** An "amount" specify is sent as this integer-cents attribute instead of `<key>Specify`. */
  specifyCents?: string;
}

export interface CollectorSection {
  id: CollectorSectionId;
  fields: readonly CollectorField[];
}

export interface CollectorSchema {
  category: CollectorCategory;
  sections: readonly CollectorSection[];
  /** `valueAmount`: the insured amount (art, card) or the total (wine, whisky). */
  value: "insured" | "total";
  /** The trading-card cap applies. */
  cap: boolean;
  /** The "Higher", "Lower" and "Previous losses" notices apply. */
  advisory: boolean;
}

// ---------------------------------------------------------------- schemas

const YES_NO: readonly CollectorOption[] = [{ value: "Yes" }, { value: "No" }];
const YES_SPECIFY_NO: readonly CollectorOption[] = [{ value: "Yes", specify: true }, { value: "No" }];
const STORAGE_BOTH_SPECIFY: readonly CollectorOption[] = [
  { value: "home", specify: true },
  { value: "external_storage", specify: true },
];
const QUANTITY: readonly CollectorOption[] = [
  { value: "1" },
  { value: "6" },
  { value: "12" },
  { value: "other", specify: "integer" },
];
const INSURED_AMOUNT_MODE: CollectorField = {
  key: "insuredAmountMode",
  type: "radio",
  required: true,
  options: [{ value: "same_as_purchase" }, { value: "other", specify: "amount" }],
  specifyCents: "insuredAmountOverrideCents",
};
const EXTERNAL: Rule = { field: "storagePlace", eq: "external_storage" };
const HOME: Rule = { field: "storagePlace", eq: "home" };

const ART: CollectorSchema = {
  category: "art",
  value: "insured",
  cap: false,
  advisory: true,
  sections: [
    {
      id: "info",
      fields: [
        {
          key: "category",
          type: "select",
          required: true,
          options: [
            { value: "painting" },
            { value: "worksOnPaper" },
            { value: "sculpture" },
            { value: "furniture" },
            { value: "ceramics" },
            { value: "metalware" },
            { value: "architecturalFeatures" },
            { value: "collectibles" },
            { value: "other", specify: true },
          ],
        },
        { key: "creator", type: "text", required: true },
        { key: "itemTitle", type: "text", required: true },
        // Required when the title is blank; the title is itself required, so
        // this never fires. Ported as-is.
        { key: "description", type: "text", required: { blank: "itemTitle" } },
        { key: "yearOfCreation", type: "text", required: true },
        { key: "dimensions", type: "text" },
        { key: "purchaseDate", type: "date", required: true },
        { key: "purchasePrice", type: "currency", required: true },
        INSURED_AMOUNT_MODE,
        { key: "hasValuationReport", type: "yesNo", required: true, options: YES_NO },
        { key: "previousLosses", type: "yesNo", required: true, options: YES_NO },
        { key: "insuredPersonName", type: "text", help: true },
      ],
    },
    {
      id: "storage",
      fields: [
        { key: "storagePlace", type: "radio", required: true, options: STORAGE_BOTH_SPECIFY },
        {
          key: "securitySystems",
          type: "multi",
          required: true,
          options: [{ value: "cctv" }, { value: "alarm" }, { value: "security_guard" }, { value: "other", specify: true }],
        },
        {
          key: "fireProtection",
          type: "multi",
          required: true,
          options: [{ value: "fire_alarm" }, { value: "fire_extinguisher" }, { value: "smoke_detector" }, { value: "sprinkler" }],
        },
      ],
    },
    {
      id: "documents",
      fields: [
        { key: "invoice", type: "upload", required: true },
        { key: "valuation_report", type: "upload", required: { field: "hasValuationReport", eq: "Yes" } },
        { key: "provenance_records", type: "upload", help: true },
        {
          key: "condition_report",
          type: "upload",
          required: { all: [EXTERNAL, { noFiles: "item_image" }] },
          requiredKey: "collector.errors.imagesOrReport",
        },
        {
          key: "item_image",
          type: "upload",
          required: { noFiles: "condition_report" },
          requiredKey: "collector.errors.imagesOrReport",
          help: true,
        },
        { key: "selfie_with_item", type: "upload", required: HOME },
      ],
    },
  ],
};

function bottleSchema(category: "wine" | "whisky"): CollectorSchema {
  const whisky = category === "whisky";
  const info: CollectorField[] = [
    whisky ? { key: "distillery", type: "text", required: true } : { key: "producer", type: "text", required: true },
    { key: "nameExpression", type: "text", required: true },
    ...(whisky
      ? ([
          { key: "ageStatement", type: "text", required: true },
          {
            key: "abvPercent",
            type: "number",
            required: true,
            range: { min: 0, max: 100, messageKey: "collector.errors.abv" },
          },
        ] satisfies CollectorField[])
      : []),
    { key: "vintage", type: "text", required: true },
    { key: "countryRegion", type: "text", required: true },
    {
      key: "bottleSize",
      type: "radio",
      required: true,
      options: whisky
        ? [{ value: "700" }, { value: "750" }, { value: "other", specify: true }]
        : [{ value: "750" }, { value: "1500" }, { value: "3000" }, { value: "other", specify: true }],
    },
    ...(whisky
      ? ([
          { key: "bottlingYear", type: "text" },
          { key: "serialNumber", type: "text" },
        ] satisfies CollectorField[])
      : []),
    { key: "pricePerBottle", type: "currency", required: true },
    { key: "quantity", type: "radio", required: true, options: QUANTITY },
    // The app shows the total for wine only; whisky shows it too on web.
    { key: "totalValue", type: "total" },
    { key: "purchaseDate", type: "date", required: true },
    {
      key: "originalPackaging",
      type: "multi",
      options: [
        { value: "box" },
        { value: "wooden_case" },
        { value: "certificate" },
        ...(whisky ? [{ value: "stopper" }] : []),
      ],
    },
  ];
  return {
    category,
    value: "total",
    cap: false,
    advisory: false,
    sections: [
      { id: "info", fields: info },
      {
        id: "storage",
        fields: [
          { key: "storagePlace", type: "radio", required: true, help: true, options: STORAGE_BOTH_SPECIFY },
          { key: "accessControl", type: "radio", required: true, help: true, options: YES_SPECIFY_NO },
          { key: "climateControl", type: "radio", required: true, help: true, options: YES_SPECIFY_NO },
        ],
      },
      {
        id: "documents",
        fields: [
          { key: "invoice", type: "upload", required: true },
          { key: "valuation_report", type: "upload" },
          { key: "storage_statement", type: "upload", revealWhen: EXTERNAL, required: EXTERNAL, help: true },
          { key: "item_image", type: "upload", required: HOME, help: true },
        ],
      },
    ],
  };
}

const TRADING_CARD: CollectorSchema = {
  category: "tradingCard",
  value: "insured",
  cap: true,
  advisory: true,
  sections: [
    {
      id: "info",
      fields: [
        { key: "gameSport", type: "text", required: true },
        { key: "setExpansion", type: "text", required: true },
        { key: "cardSubject", type: "text", required: true },
        { key: "cardNumberInSet", type: "text", required: true },
        { key: "yearOfManufacture", type: "text", required: true },
        { key: "purchaseDate", type: "date", required: true },
        { key: "purchasePrice", type: "currency", required: true },
        INSURED_AMOUNT_MODE,
        { key: "hasValuationReport", type: "yesNo", required: true, options: YES_NO },
        { key: "previousLosses", type: "yesNo", required: true, options: YES_NO },
      ],
    },
    {
      id: "grading",
      fields: [
        {
          key: "gradingCompany",
          type: "radio",
          required: true,
          options: [{ value: "psa" }, { value: "cgc" }, { value: "bgs" }, { value: "sgc" }, { value: "other", specify: true }],
        },
        { key: "assignedGrade", type: "grade", required: true },
        { key: "certificationNumber", type: "text", required: true },
      ],
    },
    {
      id: "storage",
      fields: [
        {
          key: "storagePlace",
          type: "radio",
          required: true,
          options: [{ value: "home" }, { value: "external_storage", specify: true }],
        },
        { key: "accessControl", type: "yesNo", options: YES_NO },
        { key: "climateControl", type: "yesNo", options: YES_NO },
      ],
    },
    {
      id: "documents",
      fields: [
        { key: "invoice", type: "upload", required: true },
        { key: "valuation_report", type: "upload" },
        { key: "proof_of_external_storage", type: "upload", required: EXTERNAL },
        { key: "item_image", type: "upload", required: true, help: true },
      ],
    },
  ],
};

export const COLLECTOR_SCHEMAS: Readonly<Record<CollectorCategory, CollectorSchema>> = {
  art: ART,
  wine: bottleSchema("wine"),
  whisky: bottleSchema("whisky"),
  tradingCard: TRADING_CARD,
};

// ---------------------------------------------------------------- keys

export function labelKey(category: CollectorCategory, key: string): string {
  return `collector.${category}.${key}`;
}
export function optionKey(category: CollectorCategory, field: CollectorField, value: string): string {
  return field.type === "yesNo" ? `collector.yesNo.${value}` : `collector.${category}.options.${field.key}.${value}`;
}
export function helpKey(category: CollectorCategory, key: string): string {
  return `collector.${category}.help.${key}`;
}
export function specifyKey(key: string): string {
  return `${key}Specify`;
}

/** Every i18n key the schemas reference (labels, options, help). */
export function collectorSchemaKeys(): string[] {
  const out: string[] = [];
  for (const schema of Object.values(COLLECTOR_SCHEMAS)) {
    for (const section of schema.sections) {
      out.push(`addItem.steps.${section.id}`);
      for (const f of section.fields) {
        out.push(labelKey(schema.category, f.key));
        if (f.help) out.push(helpKey(schema.category, f.key));
        if (f.requiredKey) out.push(f.requiredKey);
        if (f.range) out.push(f.range.messageKey);
        if (f.type !== "grade") for (const o of f.options ?? []) out.push(optionKey(schema.category, f, o.value));
      }
    }
  }
  return [...new Set(out)];
}

// ---------------------------------------------------------------- helpers

/** The section ids for a category, in order. */
export function collectorSections(category: CollectorCategory): CollectorSectionId[] {
  return COLLECTOR_SCHEMAS[category].sections.map((s) => s.id);
}

function section(category: CollectorCategory, id: CollectorSectionId): CollectorSection | undefined {
  return COLLECTOR_SCHEMAS[category].sections.find((s) => s.id === id);
}

function str(v: CollectorValue | undefined): string {
  return typeof v === "string" ? v : "";
}
function list(v: CollectorValue | undefined): string[] {
  return Array.isArray(v) ? v : [];
}
function isBlank(v: CollectorValue | undefined): boolean {
  return Array.isArray(v) ? v.length === 0 : !str(v).trim();
}

export function evalRule(rule: Rule, values: CollectorValues, files: FileCounts = {}): boolean {
  if ("all" in rule) return rule.all.every((r) => evalRule(r, values, files));
  if ("any" in rule) return rule.any.some((r) => evalRule(r, values, files));
  if ("blank" in rule) return isBlank(values[rule.blank]);
  if ("noFiles" in rule) return (files[rule.noFiles] ?? 0) === 0;
  const v = values[rule.field];
  const has = (x: string) => (Array.isArray(v) ? v.includes(x) : v === x);
  if ("eq" in rule) return has(rule.eq);
  return rule.in.some(has);
}

export function isFieldRequired(field: CollectorField, values: CollectorValues, files: FileCounts = {}): boolean {
  if (field.required === true) return true;
  if (!field.required) return false;
  return evalRule(field.required, values, files);
}

/** The fields of a section that are shown for these values. */
export function visibleFields(
  category: CollectorCategory,
  sectionId: CollectorSectionId,
  values: CollectorValues,
  files: FileCounts = {},
): CollectorField[] {
  return (section(category, sectionId)?.fields ?? []).filter((f) => !f.revealWhen || evalRule(f.revealWhen, values, files));
}

/** The chosen option that reveals the specify input, if any. */
export function specifyOption(field: CollectorField, values: CollectorValues): CollectorOption | undefined {
  const v = values[field.key];
  const chosen = Array.isArray(v) ? v : [str(v)];
  return field.options?.find((o) => o.specify && chosen.includes(o.value));
}

/** `YYYY-MM-DD` → UTC midnight ms, or null. */
function parseDay(value: string): number | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value.trim());
  if (!m) return null;
  const ms = Date.UTC(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  const d = new Date(ms);
  if (d.getUTCFullYear() !== Number(m[1]) || d.getUTCMonth() !== Number(m[2]) - 1 || d.getUTCDate() !== Number(m[3])) {
    return null;
  }
  return ms;
}

/** A plain decimal number ("43.5"), or null. */
export function parseNumber(value: string): number | null {
  const t = value.trim();
  if (!/^-?(\d+(\.\d*)?|\.\d+)$/.test(t)) return null;
  const n = Number(t);
  return Number.isFinite(n) ? n : null;
}

/** A whole number above 0, or null. */
export function parsePositiveInt(value: string): number | null {
  const t = value.trim();
  if (!/^\d+$/.test(t)) return null;
  const n = Number(t);
  return n > 0 && Number.isSafeInteger(n) ? n : null;
}

const toCents = (amount: number) => Math.round(amount * 100);
const round2 = (amount: number) => Math.round(amount * 100) / 100;

/** The insured amount: the "other" amount when chosen, else the purchase price. */
export function insuredAmount(values: CollectorValues): number | null {
  if (str(values.insuredAmountMode) === "other") return parseAmount(str(values[specifyKey("insuredAmountMode")]));
  return parseAmount(str(values.purchasePrice));
}

/** The field holding the insured amount (for the cap error). */
export function insuredAmountField(values: CollectorValues): string {
  return str(values.insuredAmountMode) === "other" ? specifyKey("insuredAmountMode") : "purchasePrice";
}

/** Price per bottle × quantity (wine, whisky), or null. */
export function collectorTotal(values: CollectorValues): number | null {
  const price = parseAmount(str(values.pricePerBottle));
  const q = str(values.quantity);
  const qty = q === "other" ? parsePositiveInt(str(values[specifyKey("quantity")])) : parsePositiveInt(q);
  if (price === null || qty === null) return null;
  return round2(price * qty);
}

// ---------------------------------------------------------------- notices

export type AdvisoryId = "higher" | "lower" | "previousLosses";

export interface AdvisoryNotice {
  id: AdvisoryId;
  titleKey: string;
  bodyKey: string;
  /** Where it shows: on the info section, or above the submit button. */
  placement: "info" | "submit";
}

export interface CapNotice {
  /** The amount field the error shows on. */
  field: string;
  /** The tenant cap, in major units. */
  cap: number;
  messageKey: "collector.notices.cap";
}

export interface CollectorNotices {
  cap?: CapNotice;
  advisory: AdvisoryNotice[];
}

/** `YYYY-MM-DD` three years before `today`. */
function threeYearsBefore(today: string): string {
  return `${String(Number(today.slice(0, 4)) - 3).padStart(4, "0")}${today.slice(4)}`;
}

function utcToday(): string {
  return new Date().toISOString().slice(0, 10);
}

/**
 * The trading-card cap (blocking) and the advisory review notices, as the
 * app's `tradingCardValueCapPopupTrigger` and `collectorReviewPopupTriggers`.
 * The cap comes from the currency (SGD 20,000, HKD 115,000).
 */
export function collectorNotices(
  category: CollectorCategory,
  values: CollectorValues,
  currency: string,
  today: string = utcToday(),
): CollectorNotices {
  const schema = COLLECTOR_SCHEMAS[category];
  const out: CollectorNotices = { advisory: [] };
  const insured = insuredAmount(values);
  const cap = TRADING_CARD_CAPS[currency];
  if (schema.cap && cap !== undefined && insured !== null && insured > cap) {
    out.cap = { field: insuredAmountField(values), cap, messageKey: "collector.notices.cap" };
  }
  if (!schema.advisory) return out;
  const purchase = parseAmount(str(values.purchasePrice));
  const noValuation = str(values.hasValuationReport) === "No";
  const higher = noValuation && insured !== null && purchase !== null && insured > purchase;
  // The cap is a hard stop; it replaces the value notices (the app checks it first).
  if (!out.cap) {
    if (higher) {
      out.advisory.push({ id: "higher", titleKey: "collector.notices.higher.title", bodyKey: "collector.notices.higher.body", placement: "info" });
    } else if (noValuation) {
      const date = str(values.purchaseDate);
      if (parseDay(date) !== null && date < threeYearsBefore(today)) {
        out.advisory.push({ id: "lower", titleKey: "collector.notices.lower.title", bodyKey: "collector.notices.lower.body", placement: "info" });
      }
    }
  }
  if (str(values.previousLosses) === "Yes") {
    out.advisory.push({
      id: "previousLosses",
      titleKey: "collector.notices.previousLosses.title",
      bodyKey: "collector.notices.previousLosses.body",
      placement: "submit",
    });
  }
  return out;
}

// ---------------------------------------------------------------- validation

export interface CollectorContext {
  /** Today in the tenant time zone, `YYYY-MM-DD`. */
  today: string;
  /** The tenant currency (for the trading-card cap). */
  currency: string;
  /** Files per document kind (the documents section). */
  files?: FileCounts;
}

/** Field (or `<key>Specify`) → i18n key of its error. Empty when the section is complete. */
export function validateCollectorSection(
  category: CollectorCategory,
  sectionId: CollectorSectionId,
  values: CollectorValues,
  ctx: CollectorContext,
): Record<string, string> {
  const files = ctx.files ?? {};
  const errors: Record<string, string> = {};
  for (const f of visibleFields(category, sectionId, values, files)) {
    const v = values[f.key];
    const required = isFieldRequired(f, values, files);
    const blank = isBlank(v);
    switch (f.type) {
      case "total":
        break;
      case "upload":
        if (required && (files[f.key as UploadKind] ?? 0) === 0) errors[f.key] = f.requiredKey ?? "collector.errors.files";
        break;
      case "text":
        if (required && blank) errors[f.key] = f.requiredKey ?? "collector.errors.required";
        break;
      case "number": {
        if (blank) {
          if (required) errors[f.key] = "collector.errors.required";
          break;
        }
        const n = parseNumber(str(v));
        if (n === null) errors[f.key] = "collector.errors.number";
        else if (f.range && (n < f.range.min || n > f.range.max)) errors[f.key] = f.range.messageKey;
        break;
      }
      case "currency": {
        if (blank) {
          if (required) errors[f.key] = "addItem.errors.value";
          break;
        }
        const n = parseAmount(str(v));
        if (n === null || !(n > 0)) errors[f.key] = "addItem.errors.value";
        break;
      }
      case "date": {
        if (blank) {
          if (required) errors[f.key] = "addItem.errors.dateRequired";
          break;
        }
        if (parseDay(str(v)) === null) errors[f.key] = "addItem.errors.dateRequired";
        else if (str(v) > ctx.today) errors[f.key] = "addItem.errors.dateFuture";
        break;
      }
      case "grade":
        if (blank) {
          if (required) errors[f.key] = "collector.errors.choose";
        } else if (!GRADE_OPTIONS.includes(str(v))) {
          errors[f.key] = "collector.errors.choose";
        }
        break;
      default:
        // radio, select, yesNo, multi
        if (required && blank) errors[f.key] = "collector.errors.choose";
    }
    const opt = specifyOption(f, values);
    if (opt && opt.specify !== true) {
      const sk = specifyKey(f.key);
      const s = str(values[sk]);
      if (opt.specify === "integer" && parsePositiveInt(s) === null) errors[sk] = "collector.errors.quantity";
      if (opt.specify === "amount") {
        const n = parseAmount(s);
        if (n === null || !(n > 0)) errors[sk] = "addItem.errors.value";
      }
    }
  }
  if (sectionId === "info") {
    const { cap } = collectorNotices(category, values, ctx.currency, ctx.today);
    if (cap) errors[cap.field] = cap.messageKey;
  }
  return errors;
}

// ---------------------------------------------------------------- payload

export interface CollectorCreatePayload {
  classId: number;
  classification: CollectorCategory;
  valueAmount: number;
  valueCurrency: string;
  purchasedAt: string;
  documents: ItemUploadDocument[];
  attributes: Record<string, unknown>;
}

const isoDay = (date: string) => `${date}T00:00:00.000Z`;

/**
 * `attributes` from every visible non-document field: specify inputs as
 * `<key>Specify`, currency as integer `<key>Cents` (the "other" insured
 * amount as `insuredAmountOverrideCents`), numbers and grades as numbers,
 * choices and specifies as strings. Hidden fields and blanks are dropped;
 * the total is never sent.
 */
export function buildCollectorAttributes(category: CollectorCategory, values: CollectorValues): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const s of COLLECTOR_SCHEMAS[category].sections) {
    if (s.id === "documents") continue;
    for (const f of visibleFields(category, s.id, values)) {
      const v = values[f.key];
      switch (f.type) {
        case "total":
        case "upload":
          continue;
        case "multi": {
          const items = list(v).filter((x) => f.options?.some((o) => o.value === x));
          if (items.length) out[f.key] = items;
          break;
        }
        case "currency": {
          const n = parseAmount(str(v));
          if (n !== null) out[`${f.key}Cents`] = toCents(n);
          break;
        }
        case "number":
        case "grade": {
          const n = parseNumber(str(v));
          if (n !== null) out[f.key] = n;
          break;
        }
        case "date": {
          if (parseDay(str(v)) !== null) out[f.key] = isoDay(str(v));
          break;
        }
        default: {
          const t = str(v).trim();
          if (t) out[f.key] = t;
        }
      }
      const opt = specifyOption(f, values);
      if (!opt) continue;
      const spec = str(values[specifyKey(f.key)]).trim();
      if (!spec) continue;
      if (f.specifyCents) {
        const n = parseAmount(spec);
        if (n !== null) out[f.specifyCents] = toCents(n);
      } else {
        out[specifyKey(f.key)] = spec;
      }
    }
  }
  return out;
}

/** `valueAmount` in dollars: the insured amount (art, card) or the total (wine, whisky). */
export function collectorValueAmount(category: CollectorCategory, values: CollectorValues): number {
  const v = COLLECTOR_SCHEMAS[category].value === "total" ? collectorTotal(values) : insuredAmount(values);
  return v === null ? 0 : round2(v);
}

export function buildCollectorPayload(
  category: CollectorCategory,
  values: CollectorValues,
  opts: { currency: string; documents: ItemUploadDocument[] },
): CollectorCreatePayload {
  return {
    classId: COLLECTOR_CLASS_IDS[category],
    classification: category,
    valueAmount: collectorValueAmount(category, values),
    valueCurrency: opts.currency,
    purchasedAt: isoDay(str(values.purchaseDate)),
    documents: opts.documents,
    attributes: buildCollectorAttributes(category, values),
  };
}

const NAME_KEYS: Record<CollectorCategory, readonly [string, string]> = {
  wine: ["producer", "nameExpression"],
  whisky: ["distillery", "nameExpression"],
  art: ["creator", "itemTitle"],
  tradingCard: ["cardSubject", "setExpansion"],
};

/**
 * A collector item's display name from its attributes: producer or
 * distillery + nameExpression, creator + itemTitle, cardSubject +
 * setExpansion. Undefined when none is set.
 */
export function collectorItemName(
  classification: string | null | undefined,
  attributes: Record<string, unknown> | null | undefined,
): string | undefined {
  if (!isCollectorCategory(classification) || !attributes) return undefined;
  const name = NAME_KEYS[classification]
    .map((k) => attributes[k])
    .filter((v): v is string => typeof v === "string" && !!v.trim())
    .map((v) => v.trim())
    .join(" ");
  return name || undefined;
}

// ---------------------------------------------------------------- edit mode

/** The stored item an edit starts from. */
export interface CollectorEditItem {
  _id: string;
  classification?: string;
  valueCurrency?: string;
  purchasedAt?: string | null;
  attributes?: Record<string, unknown> | null;
  documents?: readonly unknown[] | null;
}

const centsToInput = (c: unknown) => (typeof c === "number" && Number.isFinite(c) ? String(c / 100) : undefined);

/**
 * Form values from a stored item, reversing the payload transforms: cents →
 * dollars, `<key>Specify` → the specify input, `purchasedAt` → the purchase
 * date. Keys the form doesn't own are left out (they're kept on update).
 */
export function prefillFromItem(category: CollectorCategory, item: CollectorEditItem): CollectorValues {
  const a = item.attributes ?? {};
  const out: CollectorValues = {};
  const put = (k: string, v: CollectorValue | undefined) => {
    if (v !== undefined && !(typeof v === "string" && v === "")) out[k] = v;
  };
  for (const s of COLLECTOR_SCHEMAS[category].sections) {
    if (s.id === "documents") continue;
    for (const f of s.fields) {
      const raw = a[f.key];
      switch (f.type) {
        case "total":
        case "upload":
          continue;
        case "currency":
          put(f.key, centsToInput(a[`${f.key}Cents`]));
          break;
        case "date": {
          const from = typeof item.purchasedAt === "string" && item.purchasedAt ? item.purchasedAt : raw;
          put(f.key, typeof from === "string" ? from.slice(0, 10) : undefined);
          break;
        }
        case "multi":
          if (Array.isArray(raw)) put(f.key, raw.filter((x): x is string => typeof x === "string"));
          break;
        default:
          if (typeof raw === "string") put(f.key, raw);
          else if (typeof raw === "number" && Number.isFinite(raw)) put(f.key, String(raw));
      }
      if (f.options?.some((o) => o.specify)) {
        if (f.specifyCents) put(specifyKey(f.key), centsToInput(a[f.specifyCents]));
        else {
          const spec = a[specifyKey(f.key)];
          if (typeof spec === "string") put(specifyKey(f.key), spec);
        }
      }
    }
  }
  return out;
}

export interface CollectorUpdatePayload {
  id: string;
  valueAmount: number;
  purchasedAt: string;
  classification: CollectorCategory;
  /** The stored currency, else the tenant's. */
  valueCurrency: string;
  attributes: Record<string, unknown>;
  documents: readonly unknown[];
}

/**
 * `PUT /items` body for an edit: the stored attributes the form doesn't own
 * (server-owned keys such as the review reason) with the form's attributes
 * on top, and the documents passed back unchanged.
 */
export function buildCollectorUpdatePayload(
  category: CollectorCategory,
  item: CollectorEditItem,
  values: CollectorValues,
  /** The tenant currency, used when the stored item has none. */
  tenantCurrency: string,
): CollectorUpdatePayload {
  // Only keys the form doesn't own (e.g. `underwriterReviewReason`) carry over:
  // a field the edit hid or cleared must not keep its stored value.
  const owned = new Set<string>();
  for (const s of COLLECTOR_SCHEMAS[category].sections) {
    for (const f of s.fields) {
      owned.add(f.key);
      owned.add(specifyKey(f.key));
      owned.add(`${f.key}Cents`);
      if (f.specifyCents) owned.add(f.specifyCents);
    }
  }
  const kept = Object.fromEntries(Object.entries(item.attributes ?? {}).filter(([k]) => !owned.has(k)));
  return {
    id: item._id,
    valueAmount: collectorValueAmount(category, values),
    purchasedAt: isoDay(str(values.purchaseDate)),
    classification: category,
    valueCurrency: item.valueCurrency || tenantCurrency,
    attributes: { ...kept, ...buildCollectorAttributes(category, values) },
    documents: item.documents ?? [],
  };
}

/** Restore draft values: strings and string arrays only. */
export function sanitizeCollectorValues(raw: unknown): CollectorValues {
  const out: CollectorValues = {};
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return out;
  for (const [k, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === "string") out[k] = v;
    else if (Array.isArray(v) && v.every((x) => typeof x === "string")) out[k] = v as string[];
  }
  return out;
}
